import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { DoRequestResponse, GlobalActions, GlobalPut } from '@ps20-frontend/shop-integration-redux';
import {
  VersicherungssummenRequestModel,
  VersicherungssummenResponseModel,
} from '@ps20/bff-models';
import { App, BasisAdresse, VhvRelevantVorbelegungdaten } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { equalAdressen } from '@ps20-frontend/vorschlagsdaten-lib';
import { VhvActions } from '../vhvActions';
import { IAppState } from '../appState';
import {
  VersicherungssummeChangedAction,
  VersicherungssummeEnum,
  VersicherungssummenArtChangedAction,
} from '../model/vertragsoptionen.model';
import { selectVhvRelevantVorbelegungdaten } from '../../app/shared/selectors/transient-state.selector';
import { selectVersObjekt } from '../../app/shared/selectors/versObjekt.selector';
import { selectRisikoort } from '../../app/shared/selectors/risikoort.selector';
import { VersicherungssummeForLinie } from '../action-types/versicherungssummenResponseReceived';

export const VERSICHERUNGSSUMME_URL = '/privatschutz/api/versicherungssummen';

export function* watchVersicherungssummenTrigger() {
  yield takeLatest(
    VhvActions.VERSICHERUNGSSUMME_CHANGED,
    safeSaga(requestVersicherungssummenAfterChange, App.VHV_V2),
  );
  yield takeLatest(
    VhvActions.VERSSUMME_ART_CHANGED,
    safeSaga(requestVersicherungssummeOnSummenartChange, App.VHV_V2),
  );
  yield takeLatest(
    VhvActions.ZUM_ANGEBOT_BUTTON_CLICKED,
    safeSaga(getInitialVersicherungssummenOderTarifiere, App.VHV_V2),
  );
  yield takeEvery(
    VhvActions.VERSICHERUNGSSUMME_REQUEST_SUCCESSFUL_VHV,
    safeSaga(versicherungssummeRequestSuccessful, App.VHV_V2),
  );
}

export function* requestVersicherungssummeOnSummenartChange(
  action: VersicherungssummenArtChangedAction,
) {
  const verssummeArt: VersicherungssummeEnum = action.payload.verssummeArt;

  if (verssummeArt === VersicherungssummeEnum.EMPFOHLEN_VERSSUMME) {
    yield call(requestVersicherungssummen);
  }
}

export function* getInitialVersicherungssummenOderTarifiere() {
  const state: IAppState = yield select();
  const defaultVersicherungssummeNeuAbfragen: boolean =
    state.vertragsoptionen?.versicherungssumme === null;
  const vhvRelevantVorbelegungdaten = selectVhvRelevantVorbelegungdaten(state);
  const bisherigeSummeVorhanden: boolean =
    vhvRelevantVorbelegungdaten &&
    vhvRelevantVorbelegungdaten.verssumme !== null &&
    !hasAdresseChanged(state, vhvRelevantVorbelegungdaten);

  if (defaultVersicherungssummeNeuAbfragen && !bisherigeSummeVorhanden) {
    // beziehe eine neue Versicherungssumme wenn notwendig (bei Änderung der Wohnfläche) und tarifiere
    yield call(requestVersicherungssummen);
  } else if (
    state.vertragsoptionen.versicherungssummeArt === VersicherungssummeEnum.INDV_VERSSUMME
  ) {
    if (state.wertsachensummen?.length) {
      yield put(new VhvActions().requestPreis());
    } else {
      yield call(requestVersicherungssummen, state.vertragsoptionen?.versicherungssumme);
      yield put(
        new VhvActions().versicherungssummeChanged(state.vertragsoptionen?.versicherungssumme),
      );
    }
  } else if (
    defaultVersicherungssummeNeuAbfragen &&
    (bisherigeSummeVorhanden ||
      state.vertragsoptionen.versicherungssummeArt === VersicherungssummeEnum.BISHERIGE_VERSSUMME)
  ) {
    yield call(
      requestVersicherungssummen,
      state.transientState.relevantVorbelegungdaten?.verssumme,
    );
  } else if (!state.wertsachensummen) {
    yield call(requestVersicherungssummen, state.vertragsoptionen?.versicherungssumme);
  } else {
    yield put(new VhvActions().requestPreis());
  }
}

export function* requestVersicherungssummenAfterChange(action: VersicherungssummeChangedAction) {
  const versicherungssumme = action.payload.versicherungssumme;
  yield call(requestVersicherungssummen, versicherungssumme);
}

export function* requestVersicherungssummen(individuelleVersicherungssumme: number = null) {
  const state: IAppState = yield select();
  const wohnflaeche: number = state.versObjekt.wohnflaeche;
  const versicherungssummeRequest: VersicherungssummenRequestModel = {
    wohnflaeche,
    summe: individuelleVersicherungssumme,
  };

  const globalPut = GlobalPut.getInstance();
  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().doRequest({
      appId: App.VHV_V2,
      angebotsId: state.angebotsId,
      body: versicherungssummeRequest,
      url: VERSICHERUNGSSUMME_URL,
      successEvent: VhvActions.VERSICHERUNGSSUMME_REQUEST_SUCCESSFUL_VHV,
      errorEvent: null,
    }),
  );
}

export function* versicherungssummeRequestSuccessful(
  action: DoRequestResponse<VersicherungssummenResponseModel>,
) {
  const angebotsId = action.doRequestAction.payload.angebotsId;
  const state: IAppState = yield select();
  if (angebotsId !== state.angebotsId) {
    return;
  }

  const versicherungssummen = action.payload.summen.map(versicherungsumme => {
    return {
      ...versicherungsumme,
      produktlinie: versicherungsumme.produktlinie,
    } as VersicherungssummeForLinie;
  });

  const unterversicherungsverzicht = action.payload.unterversicherungsverzicht;
  if (
    hasAnyVerssummeChanged(state, versicherungssummen) ||
    state.unterversicherungsverzicht !== unterversicherungsverzicht
  ) {
    yield put(
      new VhvActions().versicherungssummenResponseReceived(
        versicherungssummen,
        unterversicherungsverzicht,
      ),
    );
  }
}

export function hasVerssummeChanged(state: IAppState, verssumme: number): boolean {
  const vorbelegteVerSumme: number = selectVhvRelevantVorbelegungdaten(state)?.verssumme;
  return verssumme !== vorbelegteVerSumme;
}

export function hasAnyVerssummeChanged(
  state: IAppState,
  versicherungssummen: VersicherungssummeForLinie[],
): boolean {
  return (
    state.versicherungssummen.length === 0 ||
    !state.versicherungssummen.every(
      oldSumme =>
        oldSumme.summe ===
        versicherungssummen.find(newSumme => newSumme.produktlinie === oldSumme.produktlinie)
          ?.summe,
    )
  );
}

export function hasAdresseChanged(
  state: IAppState,
  relevantVorbelegungdaten: VhvRelevantVorbelegungdaten,
): boolean {
  const currentWohnflaeche: number = selectVersObjekt(state)?.wohnflaeche;
  const currentRisikoort: BasisAdresse = selectRisikoort(state);
  const equalWohnflaeche = relevantVorbelegungdaten?.wohnflaeche === currentWohnflaeche;

  return (
    !equalAdressen(currentRisikoort, relevantVorbelegungdaten?.vorvertragAdresse as BasisAdresse) ||
    !equalWohnflaeche
  );
}
